import { useMemo } from "react";
import orderBy from "lodash.orderby";

import { userSelectors } from "@fitness-app/app-store";
import { PermissionKey, PermissionScope, UserRole } from "@fitness-app/data-models";
import { type ChatChannel, type ChatMember } from "@fitness-app/data-models/entities/Chat";

import { usePermissionScope } from "~/hooks/usePermission";
import { useAppSelector } from "~/store/initializeStore";

export const useChatAuthorOptions = (channel: ChatChannel): ChatMember[] => {
  const user = useAppSelector((state) => state.user.profile);
  const parentId = useAppSelector(userSelectors.getParentTrainerId);
  // W czyim imieniu wolno pisać: cały tenant (z ACCOUNT_OWNER włącznie) przy
  // ALL, zespół bez ACCOUNT_OWNER przy TEAM, poniżej — tylko boty. Wcześniej
  // czytane z roli, więc administracja wypadała do gałęzi domyślnej.
  const chatWriteScope = usePermissionScope(PermissionKey.CHAT_WRITE);

  return useMemo(() => {
    if (!user) {
      return [];
    }

    const withoutUser = Object.keys(channel.members).filter(
      (memberId) =>
        channel.members[memberId]?.uid !== user.id &&
        channel.members[memberId]?.role !== UserRole.CLIENT &&
        channel.members[memberId]?.role !== UserRole.SYSTEM &&
        !channel.members[memberId]?.disabled &&
        // `members` also holds display-only authors without access (TRE-2285).
        channel.assignedUsers.includes(memberId),
    );

    const idsWithoutUser = orderBy(withoutUser, ["role"]);

    if (chatWriteScope === PermissionScope.ALL) {
      return idsWithoutUser
        .map((memberId) => channel.members[memberId])
        .filter((member): member is ChatMember => !!member);
    }

    if (chatWriteScope === PermissionScope.TEAM) {
      return idsWithoutUser
        .filter((id) => id !== parentId)
        .map((memberId) => channel.members[memberId])
        .filter((member): member is ChatMember => !!member);
    }

    return Object.keys(channel.members)
      .filter((memberId) => channel.members[memberId]?.role === UserRole.BOT)
      .map((id) => channel.members[id])
      .filter((member): member is ChatMember => !!member);
  }, [user, channel, parentId, chatWriteScope]);
};
